import React, { useState, ReactNode, useRef, useEffect } from "react";

import { FormattedMessage } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";

interface ReadMoreTextProps {
  text: string | ReactNode;
  lineClamp?: string;
  openText?: ReactNode;
  closeText?: ReactNode;
  readMoreButtonDataTestingId?: string;
  hideButtonDataTestingId?: string;
  analyticEvent?: () => void;
  className?: string;
  btnClassNames?: string;
}

export const ReadMoreText: React.FC<ReadMoreTextProps> = ({
  text,
  lineClamp = "tw-line-clamp-3",
  readMoreButtonDataTestingId,
  hideButtonDataTestingId,
  openText = (
    <span data-testing-id={readMoreButtonDataTestingId}>
      <FormattedMessage
        id="listing.production_faq.read_more"
        defaultMessage="Read more"
      />
    </span>
  ),
  closeText = (
    <span data-testing-id={hideButtonDataTestingId}>
      <FormattedMessage
        id="listing.production_faq.hide"
        defaultMessage="Hide"
      />
    </span>
  ),
  analyticEvent = () => {},
  className = "",
  btnClassNames = "",
}) => {
  const [showAllText, setShowAllText] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);

  const textClassName = `tw-whitespace-pre-wrap ${!showAllText && lineClamp ? ` ${lineClamp}` : ""} ${className}`;

  useEffect(() => {
    if (analyticEvent && showAllText) {
      analyticEvent();
    }
  }, [showAllText, analyticEvent]);

  useEffect(() => {
    // ResizeObserver to handle dynamic content changes
    const observer = new ResizeObserver(() => {
      if (contentRef.current && !showAllText) {
        const isTextOverflowing =
          contentRef.current.scrollHeight > contentRef.current.clientHeight;
        setIsOverflowing(isTextOverflowing);
      }
    });

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    return () => observer.disconnect();
  }, [showAllText]);

  return (
    <div>
      <div
        className="read-more-text-wrapper"
        data-testing-id="descriptionLabel"
      >
        <div ref={contentRef} className={textClassName}>
          {text}
        </div>
      </div>
      {isOverflowing && (
        <Button
          className={btnClassNames}
          variant="underline"
          onClick={() => {
            setShowAllText(!showAllText);
          }}
        >
          <Text typeface="body-2-underline" className="hover:tw-text-gray-600">
            {showAllText ? closeText : openText}
          </Text>
        </Button>
      )}
    </div>
  );
};
